import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Activity } from "lucide-react";
import { Badge } from "../ui-bits";

function sentimentTone(s) {
  if (s === "bullish") return "positive";
  if (s === "bearish") return "negative";
  return "neutral";
}

function severityTone(severity) {
  if (severity === "high") return "warning";
  if (severity === "medium") return "info";
  return "neutral";
}

function impactScoreClass(score) {
  if (score >= 8) return "text-fij-red";
  if (score >= 6) return "text-fij-amber";
  return "text-fij-green";
}

const AIBadgeRow = ({ ai, eventId }) => (
  <div
    className="flex items-center gap-2 flex-wrap pt-2 border-t border-fij-border/60"
    data-testid={`event-ai-${eventId}`}
  >
    <Badge tone={sentimentTone(ai.sentiment)}>AI · {ai.sentiment || "neutral"}</Badge>
    {ai.impact_score != null && (
      <span className={`font-mono text-[10px] inline-flex items-center gap-1 ${impactScoreClass(ai.impact_score)}`}>
        <Activity size={10} /> Impact {ai.impact_score}/10
      </span>
    )}
    {ai.confidence != null && (
      <span className="font-mono text-[10px] text-fij-textMuted">· {ai.confidence}% conf</span>
    )}
  </div>
);

const TickerStrip = ({ tickers, time }) => (
  <div className="mt-auto flex items-center justify-between pt-3 border-t border-fij-border">
    <div className="flex flex-wrap gap-1">
      {tickers.map((t) => (
        <span
          key={t}
          className="font-mono text-[10px] text-fij-textDim border border-fij-border rounded-sm px-1.5 py-0.5"
        >
          {t}
        </span>
      ))}
    </div>
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-[10px] text-fij-textMuted">{time}</span>
      <ArrowUpRight size={14} className="text-fij-textMuted group-hover:text-fij-green transition-colors" />
    </div>
  </div>
);

export default function EventCard({ event }) {
  const e = event;
  // For live events with a stable `key` (headline hash), route to the internal
  // detail page so users get the full AI analysis. Keeps the source URL as a
  // secondary link on the detail page.
  const routeKey = e.live ? (e.key || e.id) : e.id;

  return (
    <Link
      to={`/events/${routeKey}`}
      data-testid={`event-card-${e.id}`}
      className="fij-card rounded-sm p-4 sm:p-5 flex flex-col gap-3 group"
    >
      <div className="flex items-center justify-between gap-2">
        <Badge tone={severityTone(e.severity)}>{e.severity}</Badge>
        <span className="font-mono text-[10px] text-fij-textMuted">{e.region} · {e.asset}</span>
      </div>

      <div>
        <h3 className="text-sm sm:text-base text-fij-text leading-snug">{e.title}</h3>
        <p className="text-xs text-fij-textDim mt-2 line-clamp-2">{e.summary}</p>
      </div>

      {e.ai && <AIBadgeRow ai={e.ai} eventId={e.id} />}

      <TickerStrip tickers={e.tickers} time={e.time} />
    </Link>
  );
}
